import ExcelJS from "exceljs";
import { getJson } from "../../lib/http";
import {
  FONT,
  INPUT_BLUE,
  MONTHS,
  NOTE_FILL,
  addCompsCuentas,
  barridoMonth,
  cap,
  excelDay,
  f,
  headerRow,
  parseText,
  section,
  wialonDate,
} from "./homologado";
import { HEADER_FILL, downloadBytes } from "./xlsx";

// Barrido nativo de Wialon: el libro «barrido wialon octubre.xlsx» tal como se
// arma a mano, pero con las unidades leídas en vivo de Wialon
// (GET /sg-sync/native-units, #927/#937) en vez de un export. A diferencia del
// homologado (datos de SG), aquí sí están las fechas reales de alta y de
// desactivación, el creador y los grupos, así que Facturable usa el criterio
// calibrado contra la factura de Gurtam: creada hasta el corte y sin desactivar
// o desactivada desde el inicio de la ventana (6 meses cuadró exacto: 6,049).
// Medido 28-oct-2026 con el corte de septiembre: este criterio sobre el export
// del 1-oct da 6,049; sobre Wialon en vivo 4 semanas después, 5,917. La
// diferencia son 147 unidades borradas desde el export (la API ya no las ve),
// 4 recreadas después del corte y 19 altas nuevas: sólo cuadra si se baja el
// primer día del mes.

export interface NativeUnit {
  name: string | null;
  creator: string | null;
  account: string | null;
  device_type: string | null;
  deactivated_at: number | null;
  active: boolean;
  uid: string | null;
  phone: string | null;
  last_message_at: number | null;
  created_at: number | null;
  groups: string[];
  custom_fields: string;
}

// El export de Wialon viene en hora de CDMX, que no cambia de horario desde 2022.
const CDMX = -6;
const text = (ms: number | null) => wialonDate(ms, CDMX);

// Filas fijas de Conciliación, las mismas del libro original.
const C = { cutoff: 5, windowMonths: 6, windowStart: 7, invoiced: 8, minUnits: 9, maxSilent: 10, recent: 11, rows: 15, calibrated: 19 };
const REF = (row: number) => `Conciliación!$B$${row}`;

const UNIT_HEADERS = [
  "Name", "Creator", "Account", "Device type", "Deactivation", "UID", "Phone", "Last message time", "Created", "Groups", "Custom fields",
  "Creada (fecha)", "Desactivada (fecha)", "Último mensaje (fecha)", "Facturable (1/0)", "Días sin reportar al corte", "Antigüedad al corte (meses)",
];

export const nativoFileName = (cutoff: Date) => `barrido wialon nativo ${barridoMonth(cutoff)}.xlsx`;

// Primer día del mes de corte menos (ventana − 1) meses: EDATE(EOMONTH(B5,-1)+1,-(B6-1)).
export const windowStart = (cutoff: Date, months: number) => new Date(cutoff.getFullYear(), cutoff.getMonth() - (months - 1), 1);

// El mismo criterio que la columna O, para ordenar Comps Cuentas como el original
// (por facturables). Compara días de calendario en hora de CDMX, como las fórmulas.
const cdmxDay = (ms: number) => {
  const d = new Date(ms + CDMX * 3600e3);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};
const localDay = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
// shortcut: una unidad con act=0 y sin dactt llega sin fecha de baja y cuenta
// como facturable, igual que en el export (Deactivation vacía). Medido 28-oct: 0.
export function isBillable(u: NativeUnit, cutoff: Date, start: Date) {
  if (u.created_at == null || cdmxDay(u.created_at) > localDay(cutoff)) return false;
  return u.deactivated_at == null || cdmxDay(u.deactivated_at) >= localDay(start);
}

export interface NativoOptions {
  cutoff: Date;
  windowMonths?: number;
  minUnits?: number;
  maxSilentDays?: number;
  recentDays?: number;
}

export function buildNativoWialon(list: NativeUnit[], opts: NativoOptions): ExcelJS.Workbook {
  const { cutoff, windowMonths = 6, minUnits = 10, maxSilentDays = 30, recentDays = 90 } = opts;
  const start = windowStart(cutoff, windowMonths);
  const wb = new ExcelJS.Workbook();
  wb.calcProperties = { fullCalcOnLoad: true };

  const sorted = [...list].sort((a, b) => (a.account ?? "").localeCompare(b.account ?? "", "es") || (a.name ?? "").localeCompare(b.name ?? "", "es"));
  const last = sorted.length + 1;
  const rng = (col: string) => `Units!$${col}$2:$${col}$${Math.max(2, last)}`;

  // ── Units ────────────────────────────────────────────────────────────────
  const units = wb.addWorksheet("Units", { views: [{ state: "frozen", ySplit: 1 }] });
  units.addRow(UNIT_HEADERS);
  units.getRow(1).height = 25.5;
  UNIT_HEADERS.forEach((_, i) => {
    const cell = units.getCell(1, i + 1);
    cell.font = { bold: true };
    cell.alignment = { vertical: "middle", wrapText: true };
    if (i >= 11) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
  });
  sorted.forEach((u, i) => {
    const r = i + 2;
    const row = units.addRow([
      u.name?.trim() || u.uid || "(sin nombre)", // nunca vacía: Filas en Units cuenta esta columna
      u.creator,
      u.account,
      u.device_type,
      text(u.deactivated_at),
      u.uid,
      u.phone,
      text(u.last_message_at),
      text(u.created_at),
      u.groups.join(", ") || null,
      u.custom_fields || null,
      f(parseText("I", r)),
      f(parseText("E", r)),
      f(parseText("H", r)),
      f(`IF(L${r}="",0,IF(AND(L${r}<=${REF(C.cutoff)},OR(M${r}="",M${r}>=${REF(C.windowStart)})),1,0))`),
      f(`IF(N${r}="","",MAX(0,${REF(C.cutoff)}-N${r}))`),
      f(`IF(L${r}="","",(${REF(C.cutoff)}-L${r})/30.4375)`),
    ]);
    ["L", "M", "N"].forEach((c) => (row.getCell(c).numFmt = "mm-dd-yy"));
    row.getCell("O").numFmt = "0";
    row.getCell("P").numFmt = "0";
    row.getCell("Q").numFmt = "0.0";
  });
  [28, 18, 34, 22, 22, 18, 14, 22, 22, 24, 18, 18, 18, 18, 18, 18, 18].forEach((w, i) => (units.getColumn(i + 1).width = w));
  units.autoFilter = `A1:Q${Math.max(1, last)}`;

  // ── Conciliación ─────────────────────────────────────────────────────────
  const k = wb.addWorksheet("Conciliación", { views: [{ showGridLines: false }] });
  [94, 18, 60].forEach((w, i) => (k.getColumn(i + 1).width = w));
  k.getCell("A1").value = "CONCILIACIÓN DE UNIDADES FACTURABLES — GURTAM vs. HOJA UNITS";
  k.getCell("A1").font = { name: FONT, size: 13, bold: true };
  k.getCell("A2").value = "Cuadre entre lo que Gurtam nos factura y lo que facturamos a nuestros clientes | Cifras en número de unidades";

  section(k, 4, "PARÁMETROS");
  const param = (row: number, label: string, value: ExcelJS.CellValue, numFmt: string, note: string, input = true) => {
    k.getCell(row, 1).value = label;
    const b = k.getCell(row, 2);
    b.value = value;
    b.numFmt = numFmt;
    if (input) b.font = { name: FONT, size: 10, color: { argb: INPUT_BLUE } };
    k.getCell(row, 3).value = note;
  };
  param(C.cutoff, "Fecha de corte (fin del periodo facturado)", excelDay(cutoff), "dd/mm/yyyy", cap(`${MONTHS[cutoff.getMonth()]} ${cutoff.getFullYear()}`));
  param(C.windowMonths, "Ventana de desactivación facturada (meses)", windowMonths, "#,##0", "Supuesto calibrado: las unidades desactivadas dentro de esta ventana siguen contando como facturables. Con 6 meses cuadró exacto con Gurtam (septiembre 2026)");
  param(C.windowStart, "Inicio de la ventana de desactivación", f(`EDATE(EOMONTH(B${C.cutoff},-1)+1,-(B${C.windowMonths}-1))`), "dd/mm/yyyy", "Calculado: primer día del mes de corte menos (ventana − 1) meses", false);
  param(C.invoiced, "Unidades facturadas por Gurtam", null, "#,##0", "Capturar el dato de la factura de Gurtam del periodo");
  param(C.minUnits, "Mín. unidades facturables por cuenta para estadísticas", minUnits, "#,##0", "Cuentas con menos unidades se listan pero no entran en máx./percentiles/mediana/mín.");
  param(C.maxSilent, "Días máx. sin mensaje para considerar unidad 'reportando'", maxSilentDays, "#,##0", "Medido contra la fecha de corte");
  param(C.recent, "Días para considerar 'alta reciente'", recentDays, "#,##0", "Altas dentro de los últimos N días antes del corte");

  const inv = `$B$${C.invoiced}`;
  section(k, 13, "CONCILIACIÓN CON LO FACTURADO POR GURTAM");
  headerRow(k, 14, ["Criterio", "Unidades", "Diferencia vs. Gurtam"]);
  const line = (row: number, label: string, formula: string, withDiff = true) => {
    k.getCell(row, 1).value = label;
    k.getCell(row, 2).value = f(formula);
    k.getCell(row, 2).numFmt = "#,##0";
    if (withDiff) {
      k.getCell(row, 3).value = f(`IF(${inv}="","",B${row}-${inv})`);
      k.getCell(row, 3).numFmt = "#,##0;(#,##0);-";
    }
  };
  line(C.rows, "Filas en Units (todas las unidades de Wialon)", `COUNTA(${rng("A")})`);
  line(16, "Creadas hasta la fecha de corte", `COUNTIFS(${rng("L")},"<="&$B$${C.cutoff})`);
  line(17, "Creadas hasta el corte y sin desactivar", `COUNTIFS(${rng("L")},"<="&$B$${C.cutoff},${rng("M")},"")`);
  line(18, "… más desactivadas en el mes del corte", `B17+COUNTIFS(${rng("L")},"<="&$B$${C.cutoff},${rng("M")},">="&(EOMONTH($B$${C.cutoff},-1)+1))`);
  line(C.calibrated, "Criterio calibrado (sin desactivar o desactivadas desde el inicio de la ventana)", `SUM(${rng("O")})`);
  for (let c = 1; c <= 3; c++) k.getCell(C.calibrated, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: NOTE_FILL } };
  line(20, "Unidades facturadas por Gurtam", `IF(${inv}="","",${inv})`, false);
  line(21, "Diferencia: criterio calibrado − Gurtam", `IF(${inv}="","",B${C.calibrated}-${inv})`, false);
  k.getCell(21, 2).numFmt = "#,##0;(#,##0);-";

  // shortcut: COUNTIFS no distingue mayúsculas y lee < > = al inicio como
  // operador; una cuenta «Grupo ABC» junto a «GRUPO ABC» o «>X» contaría de más.
  // Medido 28-oct en las 6,149 unidades: ninguna. Si aparece, escapar o fusionar.
  const accounts = new Map<string, number>();
  for (const u of sorted) {
    if (!u.account) continue;
    accounts.set(u.account, (accounts.get(u.account) ?? 0) + (isBillable(u, cutoff, start) ? 1 : 0));
  }
  const accountNames = [...accounts.keys()].sort((a, b) => accounts.get(b)! - accounts.get(a)! || a.localeCompare(b, "es"));
  const first = 7;
  // La misma fila de totales que arma addCompsCuentas; el test lo amarra (B25 → «Total / general»).
  const total = first + Math.max(1, accountNames.length);

  section(k, 23, "VERIFICACIONES");
  headerRow(k, 24, ["Verificación", "Resultado", "Estado"]);
  const check = (row: number, label: string, formula: string, state: string) => {
    k.getCell(row, 1).value = label;
    k.getCell(row, 2).value = f(formula);
    k.getCell(row, 2).numFmt = "#,##0";
    k.getCell(row, 3).value = state.startsWith("=") ? f(state.slice(1)) : state;
  };
  check(25, "Suma de unidades por cuenta (Comps Cuentas) = filas en Units", `'Comps Cuentas'!B${total}`, `=IF(B25=B${C.rows},"OK","REVISAR: unidades sin cuenta")`);
  check(26, "Suma de facturables por cuenta (Comps Cuentas) = criterio calibrado", `'Comps Cuentas'!C${total}`, `=IF(B26=B${C.calibrated},"OK","REVISAR")`);
  check(27, "Facturables según criterio calibrado = facturado por Gurtam", `B${C.calibrated}`, `=IF(${inv}="","SIN CAPTURA",IF(B27=${inv},"OK","DIFERENCIA"))`);
  check(28, "Filas sin UID (revisar posibles duplicados)", `COUNTBLANK(${rng("F")})`, "Informativo");
  check(29, "Facturables que nunca han enviado mensaje", `COUNTIFS(${rng("O")},1,${rng("N")},"")`, "Informativo");
  check(30, "Facturables con último mensaje hace más de N días (excluye los que nunca reportaron)", `COUNTIFS(${rng("O")},1,${rng("P")},">"&$B$${C.maxSilent})`, "Informativo");

  section(k, 32, "NOTAS Y METODOLOGÍA");
  [
    `Fuente: Wialon en vivo (todas las unidades que ve el token de servicio; hoja Units, ${sorted.length.toLocaleString("es-MX")} filas, cifras al ${new Date().toLocaleDateString("es-MX")}). Las columnas L:Q de Units son fórmulas auxiliares que convierten los textos de fecha.`,
    "Unidad facturable = creada hasta la fecha de corte Y (sin fecha de desactivación O desactivada desde el inicio de la ventana de desactivación).",
    "La ventana de 6 meses es un supuesto calibrado contra la factura de septiembre 2026, no una regla confirmada por Gurtam.",
    "Las fechas van en hora de CDMX, como el export de Wialon; las unidades creadas o desactivadas cerca de la medianoche del corte podrían caer en otro periodo.",
    "Es la foto de hoy: una unidad que cambió de cuenta o se desactivó después del corte sale con su dato actual, y las que se borraron de Wialon ya no aparecen. Para un mes cerrado, descargarlo el primer día del mes siguiente.",
    `Comps Cuentas: una fila por cuenta (Account). Las estadísticas solo incluyen cuentas con al menos el mínimo de unidades facturables definido en B${C.minUnits}.`,
    "Para cuadrar con lo facturado a clientes: capturar en Comps Cuentas, columna L, las unidades que se facturan a cada cliente; la columna M muestra la diferencia.",
  ].forEach((t, i) => (k.getCell(33 + i, 1).value = t));
  k.eachRow((row) => row.eachCell((c) => (c.font = { name: FONT, size: 10, ...c.font })));

  addCompsCuentas(wb, {
    accountNames,
    rng,
    first,
    ref: { cutoff: REF(C.cutoff), minUnits: REF(C.minUnits), maxSilent: REF(C.maxSilent), recent: REF(C.recent) },
    subtitle: "Cada cuenta (Account) de Wialon comparada contra el resto | Unidades, % y meses",
    asOf: f(`"Al corte del "&TEXT(${REF(C.cutoff)},"dd/mm/yyyy")&" | Unidades facturables (criterio calibrado): "&TEXT(${REF(C.calibrated)},"#,##0")`),
  });

  return wb;
}

export async function downloadNativoWialon(cutoff: Date): Promise<number> {
  // El API pide a Wialon ~6,150 unidades en 5 llamadas en serie (4 s medidos el
  // 28-oct, hasta 60 s por llamada): el corte de 30 s de `api` es muy justo.
  const { data = [] } = await getJson<{ data?: NativeUnit[] }>("/sg-sync/native-units", { platform: "wialon" }, undefined, { timeout: 180_000 });
  const bytes = new Uint8Array(await buildNativoWialon(data, { cutoff }).xlsx.writeBuffer());
  downloadBytes(nativoFileName(cutoff), bytes, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
  return data.length;
}
